import { useId, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

/** Ids + a11y wiring handed to the control inside a <Field>. */
export interface FieldIds {
  id: string;
  describedBy?: string;
  invalid: boolean;
}

export interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  id?: string;
  className?: string;
  children: (ids: FieldIds) => ReactNode;
}

/** Stable, collision-free ids (useId) + aria-describedby routing to hint/error. */
// eslint-disable-next-line react-refresh/only-export-components -- hook belongs with Field, part of the ui API
export function useFieldIds(explicit: string | undefined, hint?: string, error?: string): FieldIds {
  const auto = useId();
  const id = explicit ?? `f${auto}`;
  const describedBy =
    [error && `${id}-err`, hint && !error && `${id}-hint`].filter(Boolean).join(' ') || undefined;
  return { id, describedBy, invalid: Boolean(error) };
}

/**
 * Label + control + hint/error composition. The control is a render prop so
 * Input/Select/TextArea (and any future control) share one layout, one label
 * style, and correct aria-describedby / aria-invalid wiring.
 */
export default function Field({ label, hint, error, required, id, className, children }: FieldProps) {
  const ids = useFieldIds(id, hint, error);
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label && (
        <label htmlFor={ids.id} className="text-label text-ink">
          {label}
          {required && (
            <span className="ml-1 text-danger" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      {children(ids)}
      {error ? (
        <p id={`${ids.id}-err`} role="alert" className="text-caption text-danger">
          {error}
        </p>
      ) : (
        hint && (
          <p id={`${ids.id}-hint`} className="text-caption text-ink-faint">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

export type ControlSize = 'sm' | 'md' | 'lg';

const CONTROL_HEIGHT: Record<ControlSize, string> = {
  sm: 'h-[var(--size-control-sm)]', // 28px — dense inline filters
  md: 'h-[var(--size-control-md)]', // 32px — toolbar
  lg: 'h-[var(--size-control-lg)]', // 36px — forms default
};

/**
 * The single control skin shared by Input / Select / TextArea.
 * Focus comes from the global :focus-visible rule — no per-control ring.
 */
// eslint-disable-next-line react-refresh/only-export-components -- shared control skin, part of the ui API
export const controlClass = (size: ControlSize = 'lg', invalid = false) =>
  cn(
    'w-full bg-cream-light text-ink placeholder:text-ink-faint',
    'border rounded-[var(--radius-control)] px-3 text-sm',
    CONTROL_HEIGHT[size],
    invalid ? 'border-danger' : 'border-border-strong',
    'transition-[border-color,background-color]',
    'disabled:bg-cream-dark disabled:text-ink-disabled disabled:cursor-not-allowed',
  );
